import { useMutation } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaMutationOptions } from '@frontend/react-query-helpers';
import { useUnifiedThreadUpdater } from '../query-updaters';
import { SchemaSMSDataV4Service } from '../service';
import { UpdateGroupThreadNameIO } from '../types';

export const getUpdateGroupThreadNameMutationKey = () => ['V4', 'UpdateGroupThreadName'] as const;

type MutationContext<C = unknown> = {
  mutationContext: { didOptimisticUpdate: boolean; revertOptimisticUpdate?: () => void };
  otherContext?: C;
};

type UseUpdateGroupThreadNameMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends UpdateGroupThreadNameIO['input'] = UpdateGroupThreadNameIO['input']
> = {
  options?: LimitedSchemaMutationOptions<UpdateGroupThreadNameIO, E, C, never, RequestOverride>;
  httpOptions?: Options;
  optimisticUpdate?: boolean;
};

/**
 * A hook that returns a mutation for the `UpdateGroupThreadName` mutation endpoint.
 * It handles optimistic updates to the unified thread cache so the group name
 * is reflected immediately in the UI without waiting for the server response.
 *
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `true`.
 */
export const useUpdateGroupThreadNameMutation = <
  E = unknown,
  C = unknown,
  RequestOverride extends UpdateGroupThreadNameIO['input'] = UpdateGroupThreadNameIO['input']
>({
  options,
  httpOptions,
  optimisticUpdate = true,
}: UseUpdateGroupThreadNameMutationArgs<E, C | undefined, RequestOverride> = {}) => {
  const unifiedThreadUpdater = useUnifiedThreadUpdater({ httpOptions });

  return useMutation<UpdateGroupThreadNameIO['output'], E, RequestOverride, MutationContext<C>>({
    mutationKey: getUpdateGroupThreadNameMutationKey(),
    mutationFn: (req) => SchemaSMSDataV4Service.UpdateGroupThreadName(req, httpOptions),
    ...options,
    onMutate: async (request) => {
      const mutationContext: MutationContext<C>['mutationContext'] = {
        didOptimisticUpdate: false,
      };

      if (optimisticUpdate) {
        const upsertResult = await unifiedThreadUpdater.upsertUnifiedThread({
          matchValues: {
            threadId: request.threadId,
            locationId: request.locationId,
          },
          updater: (thread) =>
            thread
              ? {
                  ...thread,
                  groupThreadContext: thread.groupThreadContext
                    ? {
                        ...thread.groupThreadContext,
                        groupName: request.groupName,
                      }
                    : { groupName: request.groupName },
                }
              : undefined,
          isOptimistic: true,
        });

        mutationContext.didOptimisticUpdate = true;
        mutationContext.revertOptimisticUpdate = () => {
          upsertResult.revertCacheUpdate();
        };
      }

      return {
        mutationContext,
        otherContext: await options?.onMutate?.(request),
      };
    },
    onSuccess: async (response, request, context) => {
      // Apply the server-confirmed name across unified-thread cache families.
      // `upsertUnifiedThread` always updates ListUnifiedThreads; with
      // `isOptimistic=false` it also updates GetUnifiedThread and
      // GetUnifiedThreadDetails for the thread/location pair.
      await unifiedThreadUpdater.upsertUnifiedThread({
        matchValues: {
          threadId: request.threadId,
          locationId: request.locationId,
        },
        updater: (thread) =>
          thread
            ? {
                ...thread,
                groupThreadContext: thread.groupThreadContext
                  ? {
                      ...thread.groupThreadContext,
                      groupName: response.groupName ?? request.groupName,
                    }
                  : { groupName: response.groupName ?? request.groupName },
              }
            : undefined,
        isOptimistic: false,
      });

      return options?.onSuccess?.(response, request, context?.otherContext);
    },
    onError: (error, request, context) => {
      if (context?.mutationContext.didOptimisticUpdate) {
        context.mutationContext.revertOptimisticUpdate?.();
      }

      return options?.onError?.(error, request, context?.otherContext);
    },
    onSettled: (response, error, request, context) => {
      return options?.onSettled?.(response, error, request, context?.otherContext);
    },
  });
};
